import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { compact, filter, get, isArray, isEmpty, uniq } from 'lodash';
import { format } from 'date-fns';
import { Box, Tooltip } from '@mui/material';
import {
  Edit as EditIcon,
  Description as GridDetailIcon,
  Delete as DeleteIcon,
  AttachFile as AttachIcon,
} from '@mui/icons-material';
import { GridActionsCellItem, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { removeOrder } from './_api';
import { useAppInfo } from 'utils/hooks/useAppInfo';
import { settingHasValue } from 'utils/products';
import { useMuiGrid } from 'utils/hooks/useMuiGrid';
import { encryptId } from 'utils/hooks/useApp';
import { useEntityRemove } from 'utils/hooks/useEntityRemove';
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog';
import { useAppDispatch } from 'store/hooks';
import { storeConfirmationDataToStore } from 'store/reducers/appReducer';
import { useUser } from 'utils/hooks/useUser';
import styles from 'components/SearchResultsMUI/_styles';
import { MuiGrid } from 'components/MuiGrid/MuiGrid';

const muiGridKey = 'orderSearchResultsMui';

const OrderSearchResults: React.FC<any> = ({ rows, setRows, dataTour }) => {
  const { t } = useTranslation('Orders');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { hasRole } = useUser();
  const { onEntityRemove } = useEntityRemove(removeOrder, t);

  const { confirmationData, portalProductSettings } = useAppInfo();
  const showRequestingPhysician = !isEmpty(portalProductSettings)
    ? settingHasValue(portalProductSettings, 'seeRequestingPhysician', 'true')
    : false;

  const { injectColumnWidthsIntoColumns, reorderColumnsByGridSettings } = useMuiGrid(muiGridKey);

  const onCustomEntityDetail = useCallback(
    (row: any) =>
      navigate(
        `/orders/${encryptId(get(row, 'patient.identificationNumber', ''))}/${encryptId(
          get(row, 'accessionNumber', ''),
        )}/detail`,
        { state: { linkBack: get(row, 'operationType') } },
      ),
    [navigate],
  );

  const onCustomEntityUpdate = useCallback(
    (row: any) =>
      navigate(
        `/orders/${encryptId(get(row, 'patient.identificationNumber', ''))}/${encryptId(
          get(row, 'accessionNumber', ''),
        )}/edit`,
        { state: { linkBack: get(row, 'operationType') } },
      ),
    [navigate],
  );

  const onRemove = async (id: number) => await onEntityRemove(id);
  const clearAction = () => {
    dispatch(storeConfirmationDataToStore(null));
  };
  const confirmAction = async () => {
    const resp = await onRemove(get(confirmationData, 'id'));
    clearAction();
    if (resp) {
      setRows(
        filter(rows, (item: any) => item.id !== get(confirmationData, 'id')).map((item) => ({
          ...item,
          uniqId: `${item.accessionNumber}_${get(item, 'patient.identificationNumber')}`,
          canUpdate: hasRole('ROLE_MWL_UPDATE'),
        })),
      );
    }
  };

  const getName = useCallback(
    (row: any) =>
      `${get(row, 'patient.name.lastName', '') || ''} ${
        get(row, 'patient.name.firstName', '') || ''
      }`,
    [],
  );

  const {
    uniqUnitNames,
    uniqStationNames,
    uniqModalities,
    uniqPriorities,
    uniqStates,
    uniqRequestedProcedures,
  } = useMemo(() => {
    if (!isArray(rows)) {
      return {
        uniqUnitNames: [],
        uniqStationNames: [],
        uniqModalities: [],
        uniqPriorities: [],
        uniqStates: [],
        uniqRequestedProcedures: [],
      };
    }

    const allUnitNames: string[] = [];
    const allStationNames: string[] = [];
    const allModalities: string[] = [];
    const allPriorities: string[] = [];
    const allStates: string[] = [];
    const allRequestedProcedures: string[] = [];

    rows.forEach((item) => {
      allUnitNames.push(get(item, 'unit.name', ''));
      allStationNames.push(get(item, 'station.aetitle', ''));
      allModalities.push(get(item, 'modality.name', ''));
      allPriorities.push(get(item, 'priorityTranslated', ''));
      allStates.push(get(item, 'state', ''));
      allRequestedProcedures.push(get(item, 'requestedProcedure', ''));
    });

    return {
      uniqUnitNames: uniq(compact(allUnitNames)),
      uniqStationNames: uniq(compact(allStationNames)),
      uniqModalities: uniq(compact(allModalities)),
      uniqPriorities: uniq(compact(allPriorities)),
      uniqStates: uniq(compact(allStates)),
      uniqRequestedProcedures: uniq(compact(allRequestedProcedures)),
    };
  }, [rows]);

  const columns = useMemo(
    () =>
      reorderColumnsByGridSettings(
        injectColumnWidthsIntoColumns([
          {
            field: 'actions',
            headerName: t('Grid:actions'),
            type: 'actions',
            hideable: false,
            width: 160,
            headerAlign: 'left',
            align: 'left',
            renderCell: ({ row }: GridRenderCellParams) => (
              <>
                <GridActionsCellItem
                  icon={
                    <Tooltip title={t('Grid:orderDetail')}>
                      <GridDetailIcon />
                    </Tooltip>
                  }
                  label={t('Grid:orderDetail')}
                  onClick={() => onCustomEntityDetail(row)}
                  data-pw={`order-detail-${get(row, 'patient.identificationNumber')}`}
                />
                <GridActionsCellItem
                  icon={
                    <Tooltip title={t('Grid:orderEdit')}>
                      <EditIcon />
                    </Tooltip>
                  }
                  label={t('Grid:orderEdit')}
                  onClick={() => onCustomEntityUpdate(row)}
                  data-pw={`order-edit-${get(row, 'patient.identificationNumber')}`}
                  disabled={!hasRole('ROLE_MWL_UPDATE')}
                />
                {get(row, 'state', null) === 'NEW' ? (
                  <GridActionsCellItem
                    icon={
                      <Tooltip title={t('Grid:orderDelete')}>
                        <DeleteIcon />
                      </Tooltip>
                    }
                    label={t('Grid:orderDelete')}
                    onClick={() => dispatch(storeConfirmationDataToStore({ id: get(row, 'id') }))}
                    data-pw={`order-delete-${get(row, 'patient.identificationNumber')}`}
                    disabled={!hasRole('ROLE_MWL_UPDATE')}
                  />
                ) : (
                  <Box sx={{ width: '28px' }} />
                )}
                {!isEmpty(get(row, 'attachments', [])) && (
                  <GridActionsCellItem
                    icon={
                      <Tooltip title={t('Grid:mwlHasAttachments')} style={{ cursor: 'help' }}>
                        <AttachIcon />
                      </Tooltip>
                    }
                    label={t('Grid:mwlHasAttachments')}
                    data-pw={`order-attachment-${get(row, 'patient.identificationNumber')}`}
                  />
                )}
              </>
            ),
          },
          {
            field: 'originalDatetimeRequested',
            headerName: t('datetime'),
            type: 'date',
            valueGetter: (value: any, row: any) => value && new Date(value),
            valueFormatter: (value: any) => value && format(value, 'dd. MM. yyyy HH:mm'),
          },
          {
            field: 'name',
            headerName: t('name'),
            hideable: false,
            renderCell: ({ row }: GridRenderCellParams) => (
              <Tooltip title={getName(row)}>
                <Box onClick={() => onCustomEntityDetail(row)} sx={styles.gridLink}>
                  {getName(row)}
                </Box>
              </Tooltip>
            ),
            valueGetter: (value: any, row: any) => getName(row),
          },
          {
            field: 'patientId',
            headerName: t('identificationNumber'),
            valueGetter: (value: any, row: any) => get(row, 'patient.identificationNumber', ''),
          },
          { field: 'accessionNumber', headerName: t('accessionNumber') },
          {
            field: 'unit',
            headerName: t('unit'),
            type: 'singleSelect',
            valueOptions: uniqUnitNames || [],
            valueGetter: (value: any, row: any) => get(row, 'unit.name', ''),
          },
          {
            field: 'station',
            headerName: t('station'),
            type: 'singleSelect',
            valueOptions: uniqStationNames || [],
            valueGetter: (value: any, row: any) => get(row, 'station.aetitle', ''),
          },
          {
            field: 'modality',
            headerName: t('modality'),
            type: 'singleSelect',
            valueOptions: uniqModalities || [],
            valueGetter: (value: any, row: any) => get(row, 'modality.name', ''),
          },
          {
            field: 'priorityTranslated',
            headerName: t('priority'),
            type: 'singleSelect',
            valueOptions: uniqPriorities || [],
          },
          {
            field: 'state',
            headerName: t('state'),
            type: 'singleSelect',
            valueOptions: uniqStates || [],
          },
          ...(showRequestingPhysician
            ? [
                {
                  field: 'requestingPhysician',
                  headerName: t('requestingPhysician'),
                  valueGetter: (value: any, row: any) =>
                    `${get(row, 'requestingPhysician.name.lastName', '') || ''} ${
                      get(row, 'requestingPhysician.name.firstName', '') || ''
                    }`,
                },
              ]
            : []),
          {
            field: 'requestedProcedure',
            headerName: t('requestedProcedure'),
            type: 'singleSelect',
            valueOptions: uniqRequestedProcedures,
          },
        ]),
      ),
    [
      dispatch,
      getName,
      hasRole,
      injectColumnWidthsIntoColumns,
      onCustomEntityDetail,
      onCustomEntityUpdate,
      reorderColumnsByGridSettings,
      showRequestingPhysician,
      t,
      uniqModalities,
      uniqPriorities,
      uniqRequestedProcedures,
      uniqStates,
      uniqStationNames,
      uniqUnitNames,
    ],
  );

  const getRowId = (row: any) => row.uniqId;

  return (
    <>
      <MuiGrid
        gridKey={muiGridKey}
        getRowId={getRowId}
        rows={rows || []}
        columns={columns}
        dataTour={dataTour || 'orderSearchResultsGrid'}
        initialSortMode={[{ field: 'originalDatetimeRequested', sort: 'desc' }]}
      />
      {confirmationData && confirmationData.id && !confirmationData.editDialog && (
        <ConfirmationDialog
          title={t('confirmDelete')}
          open={true}
          aria-labelledby="form-dialog-title"
          fullWidth={true}
          cancelAction={clearAction}
          confirmAction={confirmAction}
        />
      )}
    </>
  );
};

export default OrderSearchResults;
